import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useCallback } from "react";
import PermissionGuard from "../components/Common/PermissionGuard";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
} from "reactstrap";
import api from "../helpers/api";
import {
  countryOptions,
  formatRoleName,
  userRoles,
  allRoles,
} from "../constants/utils";
import * as XLSX from "xlsx";
import FileModal from "../components/Common/importExportModal";
import "react-phone-number-input/style.css";
import Select from "react-select";

const Users = ({ onInvite }) => {
  // User modal state
  const [userModal, setUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
   const [filterModal, setFilterModal] = useState(false);
   const [filters, setFilters] = useState({
  role: "",
  verified: "",
  phoneCode: "",
  organizationId: ""
});
  
  // Organization modal state
  const [organizationModal, setOrganizationModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  
  const [organizations, setOrganizations] = useState([]);
  // Department selection removed - single Operations department used
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    imageUrl: "",
  });
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableRef = useRef(null);
  const [fileModal, setFileModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [forgetPasswordModal, setForgetPasswordModal] = useState({
    isOpen: false,
    loading: false,
    username: "",
    password: "",
    userId: null,
    successMessage: ""
  });
  const [forgetPasswordDropdown, setForgetPasswordDropdown] = useState({});
  const [errorModal, setErrorModal] = useState({
      isOpen: false,
      message: "",
      title: ""
    });

  const toggleDropdown = (userId) => {
    setDropdownOpen(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const toggleForgetPasswordDropdown = (userId) => {
    setForgetPasswordDropdown(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const openForgetPasswordModal = (user) => {
    setForgetPasswordModal({
      isOpen: true,
      loading: false,
      username: user.username,
      password: "",
      userId: user.id,
      successMessage: ""
    });
  };

  const handleForgetPassword = async () => {
    if (!forgetPasswordModal.username.trim() || !forgetPasswordModal.password.trim()) {
      setApiError("Both username and password are required");
      return;
    }

    setForgetPasswordModal(prev => ({ ...prev, loading: true }));
    setApiError("");

    try {
      const payload = {
        username: forgetPasswordModal.username,
        newPassword: forgetPasswordModal.password
      };

      await api.post("user/forget-password", payload);

      setForgetPasswordModal(prev => ({
        ...prev,
        loading: false,
        password: "",
        successMessage: "Password changed successfully"
      }));

      setApiError("");

      // Close modal after showing success message for 2 seconds
      setTimeout(() => {
        setForgetPasswordModal({
          isOpen: false,
          loading: false,
          username: "",
          password: "",
          userId: null,
          successMessage: ""
        });
      }, 2000);
    } catch (error) {
      console.error("Error changing password:", error);
      setApiError(error?.response?.data?.message || "Failed to change password. Please try again.");
    } finally {
      setForgetPasswordModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleAttendanceChange = async (userId, newStatus) => {
    try {
        await api.patch(`user/mark-offline?userId=${userId}`);
      reloadUsers(); // Refresh the user list
    } catch (error) {
      console.error('Failed to update attendance status:', error);
      
      // Handle 400 error for unresolved work orders
      if (error.response?.status === 400 && error.response?.data?.message) {
        setErrorModal({
          isOpen: true,
          title: "Cannot Update Attendance",
          message: error.response.data.message
        });
      } else {
        // Handle other errors
        setErrorModal({
          isOpen: true,
          title: "Error",
          message: "Failed to update attendance status. Please try again."
        });
      }
    }
  };

  const getAttendanceBadge = (status) => {
    if (status === 'ONLINE') {
      return (
        <Badge color="success" pill className="py-1 px-2 d-flex align-items-center gap-1">
          <div className="rounded-circle bg-white" style={{ width: '6px', height: '6px' }}></div>
          Online
        </Badge>
      );
    } else {
      return (
        <Badge color="secondary" pill className="py-1 px-2 d-flex align-items-center gap-1">
          <div className="rounded-circle bg-white" style={{ width: '6px', height: '6px' }}></div>
          Offline
        </Badge>
      );
    }
  };

  // Toggle user modal
  function toggleUserModal(user = null) {
    setUserModal(!userModal);
    setSelectedUser(user || {});
    setErrors({});
    setApiError("");
  }

  // Toggle organization modal
  function toggleOrganizationModal(organization = null) {
    setOrganizationModal(!organizationModal);
    setSelectedOrganization(organization || {});
    setErrors({});
    setApiError("");
  }

  const openImagePopup = (imageUrl) => {
    setImagePopup({
      isOpen: true,
      imageUrl: imageUrl,
    });
  };

  const closeImagePopup = () => {
    setImagePopup({
      isOpen: false,
      imageUrl: "",
    });
  };

  const handleUserModalClose = () => {
    setUserModal(false);
    setSelectedUser(null);
  };

  const handleOrganizationModalClose = () => {
    setOrganizationModal(false);
    setSelectedOrganization(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
    const publicURL = await getPresignedUrl(file);

        if (selectedUser) {
          setSelectedUser({ ...selectedUser, profileUrl: publicURL });
        } else if (selectedOrganization) {
          setSelectedOrganization({ ...selectedOrganization, profileUrl: publicURL });
        }
    } catch (err) {
      setApiError("❌ Error during upload:", err);
    }
  };

const getPresignedUrl = async (file) => {
  const formData = new FormData();

  formData.append("files", file);

  try {
    const response = await api.post("files/img-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const presignedUrl = response.data?.response[0]?.publicUrl;
    return presignedUrl;
  } catch (err) {
    console.error("Error getting presigned URL:", err);
    return null;
  }
};

const handleOrganizationUpdate = async (e) => {
  e.preventDefault();

  const newErrors = {};
  if (!selectedOrganization?.name?.trim())
    newErrors.name = "Organization name is required";
  if (!selectedOrganization?.address?.trim())
    newErrors.address = "Address is required";
  if (!selectedOrganization?.email?.trim())
    newErrors.email = "Email Address is required";
  if (!selectedOrganization?.contactNumber?.trim())
    newErrors.contactNumber = "Contact number is required";

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  try {
    setApiError("");

    // Remove `id` before sending
    const { id, ...organizationData } = selectedOrganization;
    const payload = [organizationData];

    const endpoint = "organization/create";
    const {data} = await api.post(endpoint, payload);
    
    const organizationId = data?.response[0]?.id;
    const userId = selectedUser?.id;

    if (organizationId && userId) {
      const assignEndpoint = `organization/assign?organizationId=${organizationId}&userId=${userId}`;
      await api.get(assignEndpoint);
    }

    reloadUsers();
    handleOrganizationModalClose();
  } catch (error) {
    setApiError(error?.response?.data?.error || "Something went wrong!");
  }
};

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

    };
    reader.readAsArrayBuffer(file);
  };

  const handleExport = () => {
    const dataToExport =
      users?.map((user) => ({
        "Profile URL": user?.profileUrl || "",
        Name: user?.name || "",
        Username: user?.username || "",
        Role: formatRoleName(user?.role) || "",
        Email: user?.email || "",
        "Contact Number": user?.contactNumber || "",
      })) || [];

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(
      wb,
      `users_export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const validateUserForm = () => {
    const newErrors = {};

    if (!selectedUser?.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!selectedUser?.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedUser.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!selectedUser?.role) {
      newErrors.role = "Role is required";
    }

    if (
      ["ROLE_REQUESTOR", "ROLE_ADMINISTRATOR", "ROLE_FULL_USER"].includes(selectedUser?.role) &&
      !selectedUser?.organization?.id
    ) {
      newErrors.organizationId = "Organization is required for organization members";
    }

    if (selectedUser?.contactNumber) {
      const phoneNumber = selectedUser.contactNumber.replace(/\D/g, "");
      if (phoneNumber.length < 7) {
        newErrors.contactNumber = "Phone number must be at least 7 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUserUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");

    if (!validateUserForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const userPayload = {
        id: selectedUser?.id,
        name: selectedUser?.name,
        email: selectedUser?.email,
        profileUrl: selectedUser?.profileUrl,
        phoneCode: selectedUser?.phoneCode,
        contactNumber: selectedUser?.contactNumber,
        username: selectedUser?.username,
        role: selectedUser?.role,
        specialtyAndQualification: selectedUser?.specialtyAndQualification,
        organizationId: selectedUser?.organizationId || selectedUser?.organization?.id || null,
      };

      await api.put("user/update-user", userPayload);

      handleUserModalClose();
      reloadUsers();
    } catch (error) {
      console.error("Error during submission:", error);
      setApiError(error?.response?.data?.message || "An error occurred during submission");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchOrganizations = async () => {
    const { data } = await api.get("organization/all");
    setOrganizations(data.response);
  };

  // fetchDepartments removed - single Operations department used

  const handleDelete = async () => {
    try {
      await api.delete(`user/delete-user?requestId=${selectedUser?.id}`);
      reloadUsers();
      setDeleteModal(false);
    } catch (error) {
      console.error("Error deleting user:", error);
      setApiError("Failed to delete user");
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setUsers([]);
    setCount(10);
    setHasMore(true);
  };

const reloadUsers = useCallback(async () => {
  setLoading(true);
  setCount(10); // Reset count when filters change
  try {
    // Build query string based on filters
    const queryParams = new URLSearchParams();
    
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.verified) queryParams.append('verified', filters.verified);
    if (filters.phoneCode) queryParams.append('phoneCode', filters.phoneCode);
    if (filters.organizationId) queryParams.append('organizationId', filters.organizationId);
    if (searchTerm) queryParams.append('search', searchTerm);

    const { data } = await api.get(
      `user/get-all?${queryParams.toString()}`
    );
    setUsers(data.response || []);
    setHasMore(data.response.length >= 10); // Update based on initial count
  } catch (error) {
    console.error("Error reloading users:", error);
  } finally {
    setLoading(false);
  }
}, [filters, searchTerm]);

  const loadMoreUsers = async () => {
  if (loading || !hasMore) return;

  setLoading(true);
  try {
    const newCount = count + 10;
    
    // Build query string with all current filters
    const queryParams = new URLSearchParams();
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.verified) queryParams.append('verified', filters.verified);
    if (filters.phoneCode) queryParams.append('phoneCode', filters.phoneCode);
    if (filters.organizationId) queryParams.append('organizationId', filters.organizationId);
    if (searchTerm) queryParams.append('search', searchTerm);
    queryParams.append('count', newCount);

    const { data } = await api.get(
      `user/get-all?${queryParams.toString()}`
    );
    
    setUsers(data.response || []);
    setCount(newCount);
    setHasMore(data.response.length === newCount);
  } catch (error) {
    console.error("Error loading more users:", error);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    reloadUsers();
  }, [reloadUsers]);

  useEffect(() => {
    fetchOrganizations();
    // fetchDepartments removed - single Operations department
  }, []);

  return (
    <>
      <div className="mb-4 d-flex align-items-center gap-3">
        {onInvite && (
          <PermissionGuard permission="invitation:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2"
              onClick={onInvite}
            >
              <FeatherIcon icon="user-plus" size={18} />
              Invite User
            </button>
          </PermissionGuard>
        )}
        <div style={{ minWidth: "300px", flexGrow: 1, maxWidth: "500px" }}>
          <Input
            type="text"
            placeholder="Search Users..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>
      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">Users</h3>
          <div className="d-flex align-items-center gap-2">
                <button
              className="btn btn-soft-primary p-2"
              onClick={() => setFilterModal(true)}
              title="Filter assets"
            >
              <FeatherIcon icon="filter" size={18} />
            </button>
            <PermissionGuard permission="user:create">
              <button
                className="btn btn-soft-info p-2"
                onClick={() => setFileModal(true)}
                title="Import/Export transactions"
              >
                <FeatherIcon icon="file-text" size={18} />
              </button>
            </PermissionGuard>
          </div>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            ref={tableRef}
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: "0.1rem",
                borderCollapse: "separate",
              }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "white",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Contact No.</th>
                  <th>Edit</th>
                  <th>Delete</th>
                  <th>More</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {users?.map((user, ind) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {user?.profileUrl && (
                            <img
                              alt="profile-pic"
                              src={user.profileUrl}
                              height={27}
                              width={27}
                              className="rounded-circle"
                              style={{ cursor: "pointer", objectFit: "cover" }}
                              onClick={() => openImagePopup(user.profileUrl)}
                            />
                          )}
                          {user.name}
                        </div>
                      </td>
                      <td>{user.username}</td>
                      <td>{formatRoleName(user?.role)}</td>
                      <td>{user.email}</td>
                      <td>{user.contactNumber}</td>
                      <PermissionGuard permission="user:update">
                        <td
                          className="text-warning"
                          role="button"
                          onClick={() => {
                            toggleUserModal(user);
                          }}
                        >
                          {" "}
                          <FeatherIcon icon="edit" />
                        </td>
                      </PermissionGuard>
                      <PermissionGuard permission="user:delete">
                        <td
                          className="text-danger"
                          role="button"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteModal(true);
                          }}
                        >
                          <FeatherIcon icon="trash-2" />
                        </td>
                      </PermissionGuard>
                      <td>
                        <Dropdown
                          isOpen={forgetPasswordDropdown[user.id] || false}
                          toggle={() => toggleForgetPasswordDropdown(user.id)}
                          direction="down"
                        >
                          <DropdownToggle
                            tag="div"
                            className="d-inline-block"
                            style={{ cursor: 'pointer' }}
                          >
                            <FeatherIcon icon="more-vertical" size={18} className="text-muted" />
                          </DropdownToggle>
                          <DropdownMenu container="body">
                            <DropdownItem
                              className="d-flex align-items-center gap-2"
                              onClick={() => openForgetPasswordModal(user)}
                            >
                              <FeatherIcon icon="key" size={14} />
                              Forget Password
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
            {loading && (
              <div className="text-center py-3">
                <Spinner color="primary" />
              </div>
            )}
            {!loading && users.length === 0 && (
              <div className="text-center py-3">No users found</div>
            )}
          </div>
         {hasMore && users.length > 0 && (
              <div className="text-center mt-3">
                <Button
                  color="primary"
                  onClick={loadMoreUsers}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          {!hasMore && users.length > 0 && (
            <div className="text-center py-2 mt-3 text-muted">
              No more users to load
            </div>
          )}
        </CardBody>
      </Card>

      {/* User Modal */}
    <Modal
        size="lg"
        isOpen={userModal}
        toggle={() => {
          toggleUserModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              Edit User
            </h5>
            <button
              onClick={handleUserModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleUserUpdate}>
              <Row className="mb-4">
                <label
                  htmlFor="horizontal-name-input"
                  className="col-sm-3 col-form-label"
                >
                  Name
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedUser?.name || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        name: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="Enter name"
                    id="horizontal-name-input"
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>

            <Row className="mb-4">
              <label
                htmlFor="horizontal-contact-input"
                className="col-sm-3 col-form-label"
              >
                Contact Number
              </label>
              <Col sm={9}>
                <div className="d-flex gap-2">
                  {/* Country Code Select */}
                  <Select
                    options={countryOptions}
                    value={countryOptions.find((c) =>
                      selectedUser?.phoneCode === c.dialCode.replace("+", "")
                    )}
                    onChange={(selected) => {
                      const dialCode = selected?.dialCode.replace("+", "") || "";
                      setSelectedUser({
                        ...selectedUser,
                        phoneCode: dialCode
                      });
                    }}
                    filterOption={(option, inputValue) => {
                      if (!inputValue) return true;
                      const searchText = inputValue.toLowerCase();
                      const labelText = option.data.labelText?.toLowerCase() || '';
                      const name = option.data.name?.toLowerCase() || '';
                      const dialCode = option.data.dialCode?.toLowerCase() || '';
                      return labelText.includes(searchText) ||
                            name.includes(searchText) ||
                            dialCode.includes(searchText);
                    }}
                    className="flex-grow-0"
                    styles={{
                      control: (base) => ({
                        ...base,
                        width: "150px",
                      }),
                    }}
                    isClearable
                    placeholder="Code"
                  />

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    className={`form-control flex-grow-1 ${
                      errors.contactNumber ? "is-invalid" : ""
                    }`}
                    value={selectedUser?.contactNumber || ""}
                    onChange={(e) => {
                      const phone = e.target.value.replace(/\D/g, "");
                      setSelectedUser({
                        ...selectedUser,
                        contactNumber: phone
                      });
                    }}
                    placeholder="Phone number"
                  />
                </div>
                {errors.contactNumber && (
                  <small className="text-danger">
                    {errors.contactNumber}
                  </small>
                )}
              </Col>
            </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-email-input"
                  className="col-sm-3 col-form-label"
                >
                  Email
                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedUser?.email || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        email: e.target.value,
                      })
                    }
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Enter email address"
                    id="horizontal-email-input"
                  />
                  {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <label
                  htmlFor="horizontal-role-input"
                  className="col-sm-3 col-form-label"
                >
                  Role
                </label>
                <Col sm={9}>
                  <select
                    className={`form-select ${
                      errors.role ? "is-invalid" : ""
                    }`}
                    value={selectedUser?.role || ""}
                    onChange={(e) =>
                      setSelectedUser({
                        ...selectedUser,
                        role: e.target.value,
                      })
                    }
                    id="horizontal-role-input"
                  >
                    <option value="">Select Role</option>
                    {allRoles.map((role) => (
                      <option key={role} value={role}>
                        {formatRoleName(role)}
                      </option>
                    ))}
                  </select>
                  {errors.role && (
                    <small className="text-danger">{errors.role}</small>
                  )}
                </Col>
              </Row>

              {["ROLE_REQUESTOR", "ROLE_ADMINISTRATOR", "ROLE_FULL_USER"].includes(
                selectedUser?.role
              ) && (
                <Row className="mb-4">
                  <label
                    htmlFor="horizontal-organization-input"
                    className="col-sm-3 col-form-label"
                  >
                    Organization
                  </label>
                  <Col sm={9}>
                    <select
                      className={`form-select ${
                        errors.organizationId ? "is-invalid" : ""
                      }`}
                      value={selectedUser?.organization?.id || ""}
                      onChange={(e) => {
                        const selectedOrganization = organizations.find(
                          (h) => h.id === Number(e.target.value)
                        );
                        setSelectedUser({
                          ...selectedUser,
                          organization: selectedOrganization || null,
                          organizationId: selectedOrganization?.id || null,
                        });
                      }}
                    >
                      <option value="">Select</option>
                      {organizations?.map((organization) => (
                        <option key={organization.id} value={organization.id}>
                          {organization.name}
                        </option>
                      ))}
                    </select>
                    {errors.organizationId && (
                      <small className="text-danger">{errors.organizationId}</small>
                    )}
                  </Col>
                </Row>
              )}

              <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Processing..." : "Update"}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Organization Modal */}
      <Modal
        size="lg"
        isOpen={organizationModal}
        toggle={() => {
          toggleOrganizationModal();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title" id="myLargeModalLabel">
              {selectedOrganization?.id && selectedOrganization.id !== -1 ? "Edit Organization" : "Add Organization"}
            </h5>
            <button
              onClick={handleOrganizationModalClose}
              type="button"
              className="btn-close"
              data-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleOrganizationUpdate}>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  Profile Picture
                </label>
                <Col sm={9}>
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                  />
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  Name
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedOrganization?.name || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        name: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder="Enter Name of the Organization"
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  Address
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={selectedOrganization?.address || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        address: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder="Enter the Address"
                  />
                   {errors.address && (
                    <small className="text-danger">{errors.address}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  Email
                </label>
                <Col sm={9}>
                  <input
                    type="email"
                    value={selectedOrganization?.email || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        email: e.target.value,
                      })
                    }
                    className="form-control"
                    placeholder="Enter the Email Address"
                  />
                     {errors.email && (
                    <small className="text-danger">{errors.email}</small>
                  )}
                </Col>
              </Row>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  Contact Number
                </label>
                <Col sm={9}>
                  <input
                    className="form-control"
                    value={selectedOrganization?.contactNumber || ""}
                    onChange={(e) =>
                      setSelectedOrganization({
                        ...selectedOrganization,
                        contactNumber: e.target.value,
                      })
                    }
                    type="tel"
                    placeholder="Enter the Contact Number"
                  />
                         {errors.contactNumber && (
                    <small className="text-danger">
                      {errors.contactNumber}
                    </small>
                  )}
                </Col>
              </Row>
                <div className="mt-4">
                {apiError && (
                  <div className="alert alert-danger">{apiError}</div>
                )}
                <button
                  type="submit"
                  className="btn btn-success w-lg transition-all hover-grow pulse-on-hover"
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">Confirm Delete</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(false)}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          <p>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={imagePopup.isOpen}
        toggle={closeImagePopup}
        centered
        size="md"
      >
        <div className="modal-header">
          <button
            type="button"
            className="btn-close"
            onClick={closeImagePopup}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body text-center">
          <img
            src={imagePopup.imageUrl}
            alt="Profile"
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              borderRadius: "8px",
            }}
          />
        </div>
      </Modal>

       {/* User Filter Modal */}
<Modal
  isOpen={filterModal}
  toggle={() => setFilterModal(false)}
  size="md"
  className="fade-scale-modal"
  backdropClassName="modal-backdrop-blur"
  centered
>
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ duration: 0.2 }}
  >
    <div className="modal-header text-white">
      <h5 className="modal-title fs-4">Filter Users</h5>
      <button
        type="button"
        className="btn-close btn-close-white"
        onClick={() => setFilterModal(false)}
        aria-label="Close"
      ></button>
    </div>

    <div className="modal-body p-4">
      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="filterRole" className="form-label fw-medium">
              Role
            </label>
            <select
              id="filterRole"
              className="form-select"
              value={filters.role}
              onChange={(e) =>
                setFilters({ ...filters, role: e.target.value })
              }
            >
              <option value="">All Roles</option>
              {Object.values(userRoles).flat().map((role) => (
                <option key={role} value={role}>
                  {formatRoleName(role)}
                </option>
              ))}
            </select>
          </div>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="filterVerified" className="form-label fw-medium">
              Verification Status
            </label>
            <select
              id="filterVerified"
              className="form-select"
              value={filters.verified}
              onChange={(e) =>
                setFilters({ ...filters, verified: e.target.value })
              }
            >
              <option value="">All Users</option>
              <option value="true">Verified Only</option>
              <option value="false">Not Verified Only</option>
            </select>
          </div>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="filterPhoneCode" className="form-label fw-medium">
              Phone Country Code
            </label>
            <Select
              id="filterPhoneCode"
              options={countryOptions}
              value={countryOptions.find(opt => opt.dialCode.replace('+', '') === filters.phoneCode)}
              onChange={(selected) => 
                setFilters({ 
                  ...filters, 
                  phoneCode: selected ? selected.dialCode.replace('+', '') : '' 
                })
              }
              isClearable
              placeholder="Filter by country code"
              getOptionLabel={(option) => `${option.name} (${option.dialCode})`}
              getOptionValue={(option) => option.dialCode.replace('+', '')}
              className="basic-single"
              classNamePrefix="select"
            />
          </div>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="filterOrganization" className="form-label fw-medium">
              Organization
            </label>
            <select
              id="filterOrganization"
              className="form-select"
              value={filters.organizationId}
              onChange={(e) =>
                setFilters({ ...filters, organizationId: e.target.value })
              }
            >
              <option value="">All Organizations</option>
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
          </div>
        </Col>
      </Row>
    </div>

    <div className="modal-footer bg-light border-top-0">
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => {
          setFilters({
            role: "",
            verified: "",
            phoneCode: "",
            organizationId: ""
          });
          setFilterModal(false);
        }}
      >
        Clear Filters
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          setFilterModal(false);
          reloadUsers();
        }}
      >
        Apply Filters
      </button>
    </div>
  </motion.div>
</Modal>

      <FileModal
        title="Users"
        isOpen={fileModal}
        toggle={() => setFileModal(false)}
        data={organizations}
        importNeed={false}
        error={apiError}
        loading={loading}
        onImport={handleImport}
        onExport={handleExport}
        importLabel="Import Users from Excel"
        exportLabel="Export Users to Excel"
      />

      {/* Forget Password Modal */}
      <Modal
        isOpen={forgetPasswordModal.isOpen}
        toggle={() => setForgetPasswordModal(prev => ({ ...prev, isOpen: false }))}
        centered
        size="md"
      >
        <div className="modal-header">
          <h5 className="modal-title">
            <FeatherIcon icon="key" className="me-2" />
            Forget Password
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setForgetPasswordModal(prev => ({ ...prev, isOpen: false }))}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          <Row className="mb-3">
            <label className="col-sm-3 col-form-label">Username</label>
            <Col sm={9}>
              <input
                type="text"
                className="form-control"
                value={forgetPasswordModal.username}
                onChange={(e) => setForgetPasswordModal(prev => ({
                  ...prev,
                  username: e.target.value
                }))}
                readOnly
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <label className="col-sm-3 col-form-label">New Password</label>
            <Col sm={9}>
              <input
                type="password"
                className="form-control"
                value={forgetPasswordModal.password}
                onChange={(e) => setForgetPasswordModal(prev => ({
                  ...prev,
                  password: e.target.value
                }))}
                placeholder="Enter new password"
              />
            </Col>
          </Row>
          {apiError && (
            <div className="alert alert-danger">{apiError}</div>
          )}
          {forgetPasswordModal.successMessage && (
            <div className="alert alert-success">{forgetPasswordModal.successMessage}</div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setForgetPasswordModal(prev => ({ ...prev, isOpen: false }))}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleForgetPassword}
            disabled={forgetPasswordModal.loading}
          >
            {forgetPasswordModal.loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Changing...
              </>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </Modal>

      {/* Work Order Error Modal */}
       <Modal isOpen={errorModal.isOpen} toggle={() => setErrorModal({ isOpen: false, message: "", title: "" })} centered>
        <div className="modal-header">
          <h5 className="modal-title text-danger">
            <FeatherIcon icon="alert-circle" className="me-2" />
            {errorModal.title}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorModal({ isOpen: false, message: "", title: "" })}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning d-flex align-items-center">
            <FeatherIcon icon="alert-triangle" className="me-2" />
            <div>
              {errorModal.message}
            </div>
          </div>
          <p className="mb-0 text-muted">
            Please assign the unresolved work orders to another technician before updating the attendance status.
          </p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setErrorModal({ isOpen: false, message: "", title: "" })}
          >
            Understood
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Users;